/**
 * AddUserModal — Create a team member directly (no invitation flow).
 *
 * The server auto-generates a temporary password which is surfaced in
 * TempPasswordModal immediately after a successful creation. Password
 * is never stored in component state beyond what TempPasswordModal receives
 * on a single render, and is cleared from state when that modal closes.
 */

import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { SUPER_ADMIN_ROLE_NAME } from '@upskill/shared';
import { Modal } from '../../components/ui/Modal';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { TempPasswordModal } from '../../components/ui/TempPasswordModal';
import userService from '../../services/api/userService';
import roleService from '../../services/api/roleService';
import { getLocations } from '../../services/api/locationService';
import { useAuthStore } from '../../stores/authStore';
import { useIsSuperAdmin } from '../../hooks/useIsSuperAdmin';

// ============================================================================
// Styled Components (mirrors InviteModal)
// ============================================================================

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[2]};
  margin-bottom: ${({ theme }) => theme.spacing[4]};
`;

const FieldLabel = styled.label`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const RequiredMark = styled.span`
  color: ${({ theme }) => theme.colors.status.error.text};
  margin-left: 2px;
`;

const SelectField = styled.select<{ $hasError?: boolean }>`
  height: 44px;
  padding: 0 ${({ theme }) => theme.spacing[3]};
  border: 1px solid
    ${({ theme, $hasError }) =>
      $hasError ? theme.colors.status.error.text : theme.colors.borders.light};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: ${({ theme }) => theme.colors.backgrounds.white};
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  width: 100%;
  cursor: pointer;
  transition: ${({ theme }) => theme.transitions.fast};

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary.base};
    box-shadow: ${({ theme }) => theme.shadows.ring};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    background: ${({ theme }) => theme.colors.backgrounds.light};
  }
`;

const FooterNote = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-top: ${({ theme }) => theme.spacing[4]};
  margin-bottom: 0;
`;

const CheckboxRow = styled.label`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing[2]};
  cursor: pointer;

  input[type='checkbox'] {
    width: 16px;
    height: 16px;
    margin-top: 2px;
    accent-color: ${({ theme }) => theme.colors.primary.base};
    cursor: pointer;
    flex-shrink: 0;
  }
`;

const CheckboxLabelText = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const CheckboxHelperText = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.secondary};
  margin: ${({ theme }) => theme.spacing[1]} 0 0 ${({ theme }) => `calc(16px + ${theme.spacing[2]})`};
`;

const ModalFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing[3]};
  padding-top: ${({ theme }) => theme.spacing[4]};
  border-top: 1px solid ${({ theme }) => theme.colors.borders.light};
  margin-top: ${({ theme }) => theme.spacing[2]};
`;

const MutationErrorBanner = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.status.error.text};
  margin: 0 0 ${({ theme }) => theme.spacing[3]};
`;

const ProvisioningWarningBanner = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.status.warning.text};
  background: ${({ theme }) => theme.colors.status.warning.bg};
  border: 1px solid ${({ theme }) => theme.colors.status.warning.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: ${({ theme }) => theme.spacing[3]};
  margin: 0 0 ${({ theme }) => theme.spacing[3]};
`;

// ============================================================================
// Types
// ============================================================================

interface AddUserModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface FormErrors {
  email?: string;
  firstName?: string;
  lastName?: string;
}

// ============================================================================
// Component
// ============================================================================

export const AddUserModal = ({ isOpen, onClose }: AddUserModalProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const isSuperAdmin = useIsSuperAdmin();

  // Track the current open cycle so late callbacks from a previous cycle are ignored
  const openCycleRef = useRef(0);

  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [roleId, setRoleId] = useState('');
  const [primaryCenterId, setPrimaryCenterId] = useState('');
  const [activateLogin, setActivateLogin] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});
  const [mutationError, setMutationError] = useState<string | null>(null);

  // Temp password state — only populated after successful creation, cleared on modal close.
  // A non-null value also acts as the open flag for TempPasswordModal.
  const [tempPassword, setTempPassword] = useState<string | null>(null);
  const [tempPasswordLabel, setTempPasswordLabel] = useState<string>('');
  // Set when the user was created but Entra provisioning failed — the surfaced
  // password (if any) won't work to sign in until retried via Reset Password.
  const [provisioningWarning, setProvisioningWarning] = useState(false);

  const { data: rolesData } = useQuery({
    queryKey: ['roles'],
    queryFn: () => roleService.getRoles({ limit: 100 }),
    enabled: isOpen,
  });

  const { data: locationsData } = useQuery({
    queryKey: ['locations', 'all-for-members'],
    queryFn: () => getLocations({ limit: 200 }),
    enabled: isOpen,
  });
  const locations = locationsData?.data ?? [];

  const createMutation = useMutation<
    { result: Awaited<ReturnType<typeof userService.createMember>>; cycle: number },
    Error,
    void,
    { cycle: number }
  >({
    onMutate: () => ({ cycle: openCycleRef.current }),
    mutationFn: () =>
      userService
        .createMember({
          email: email.trim(),
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          roleIds: roleId ? [roleId] : [],
          ...(primaryCenterId ? { primaryCenterId } : {}),
          activateLogin,
          // Super admins are not implicitly org-scoped — pass the org from their session
          ...(isSuperAdmin && user?.organizationId ? { organizationId: user.organizationId } : {}),
        })
        .then((result) => ({ result, cycle: openCycleRef.current })),
    onSuccess: ({ result, cycle }) => {
      // Discard callbacks from a previous open cycle (modal was closed before response)
      if (cycle !== openCycleRef.current) return;
      queryClient.invalidateQueries({ queryKey: ['members'] });
      if (result.entraProvisioned === false) setProvisioningWarning(true);
      if (result.temporaryPassword) {
        setTempPassword(result.temporaryPassword);
        setTempPasswordLabel(`${result.firstName} ${result.lastName} (${result.email})`);
      } else if (result.entraProvisioned !== false) {
        handleClose();
      }
    },
    onError: (_err, _vars, ctx) => {
      // Guard against stale error from a prior open cycle reaching an already-closed modal
      if (ctx?.cycle !== openCycleRef.current) return;
      setMutationError(t('addUser.errorFallback'));
    },
  });

  const resetForm = () => {
    setEmail('');
    setFirstName('');
    setLastName('');
    setRoleId('');
    setPrimaryCenterId('');
    setActivateLogin(false);
    setErrors({});
    setMutationError(null);
    setProvisioningWarning(false);
  };

  const handleClose = () => {
    // Bump the cycle so any in-flight callback from this open cycle is ignored
    openCycleRef.current += 1;
    createMutation.reset();
    resetForm();
    onClose();
  };

  const handleTempPasswordClose = () => {
    // Clear sensitive data, then close
    setTempPassword(null);
    setTempPasswordLabel('');
    // If provisioning also failed, leave the warning visible in the underlying
    // modal (with its own Close action) instead of dismissing everything at once.
    if (!provisioningWarning) handleClose();
  };

  const validate = (): boolean => {
    const newErrors: FormErrors = {};

    if (!email.trim()) {
      newErrors.email = t('addUser.requiredEmail');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      newErrors.email = t('addUser.invalidEmail');
    }

    if (!firstName.trim()) {
      newErrors.firstName = t('addUser.requiredFirstName');
    }

    if (!lastName.trim()) {
      newErrors.lastName = t('addUser.requiredLastName');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    setMutationError(null);
    if (!validate()) return;
    createMutation.mutate();
  };

  const roles = (rolesData?.data ?? []).filter(
    (r) => !(r.organizationId === null && r.name === SUPER_ADMIN_ROLE_NAME),
  );

  return (
    <>
      <Modal isOpen={isOpen} onClose={handleClose} title={t('addUser.modalTitle')} size="sm">
        {mutationError && <MutationErrorBanner role="alert">{mutationError}</MutationErrorBanner>}
        {provisioningWarning && (
          <ProvisioningWarningBanner role="alert">
            {t('addUser.entraProvisioningWarning')}
          </ProvisioningWarningBanner>
        )}

        {/* Email */}
        <FormField>
          <FieldLabel htmlFor="add-user-email">
            {t('addUser.labelEmail')} <RequiredMark>*</RequiredMark>
          </FieldLabel>
          <Input
            id="add-user-email"
            type="email"
            placeholder={t('addUser.placeholderEmail')}
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (errors.email) setErrors((prev) => ({ ...prev, email: undefined }));
            }}
            error={errors.email}
            fullWidth
          />
        </FormField>

        {/* First Name */}
        <FormField>
          <FieldLabel htmlFor="add-user-first-name">
            {t('addUser.labelFirstName')} <RequiredMark>*</RequiredMark>
          </FieldLabel>
          <Input
            id="add-user-first-name"
            type="text"
            placeholder={t('addUser.placeholderFirstName')}
            value={firstName}
            onChange={(e) => {
              setFirstName(e.target.value);
              if (errors.firstName) setErrors((prev) => ({ ...prev, firstName: undefined }));
            }}
            error={errors.firstName}
            fullWidth
          />
        </FormField>

        {/* Last Name */}
        <FormField>
          <FieldLabel htmlFor="add-user-last-name">
            {t('addUser.labelLastName')} <RequiredMark>*</RequiredMark>
          </FieldLabel>
          <Input
            id="add-user-last-name"
            type="text"
            placeholder={t('addUser.placeholderLastName')}
            value={lastName}
            onChange={(e) => {
              setLastName(e.target.value);
              if (errors.lastName) setErrors((prev) => ({ ...prev, lastName: undefined }));
            }}
            error={errors.lastName}
            fullWidth
          />
        </FormField>

        {/* Role (optional) */}
        <FormField>
          <FieldLabel htmlFor="add-user-role">{t('addUser.labelRole')}</FieldLabel>
          <SelectField
            id="add-user-role"
            value={roleId}
            onChange={(e) => setRoleId(e.target.value)}
          >
            <option value="">{t('addUser.placeholderRole')}</option>
            {roles.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
              </option>
            ))}
          </SelectField>
        </FormField>

        {/* Location (optional) */}
        <FormField>
          <FieldLabel htmlFor="add-user-location">{t('addUser.labelLocation')}</FieldLabel>
          <SelectField
            id="add-user-location"
            value={primaryCenterId}
            onChange={(e) => setPrimaryCenterId(e.target.value)}
          >
            <option value="">{t('addUser.placeholderLocation')}</option>
            {locations.map((loc) => (
              <option key={loc.id} value={loc.id}>
                {loc.name}
              </option>
            ))}
          </SelectField>
        </FormField>

        {/* Grant login access */}
        <FormField>
          <CheckboxRow>
            <input
              type="checkbox"
              id="add-user-activate-login"
              checked={activateLogin}
              onChange={(e) => setActivateLogin(e.target.checked)}
            />
            <CheckboxLabelText>Grant login access immediately</CheckboxLabelText>
          </CheckboxRow>
          <CheckboxHelperText>
            When checked, the user can sign in with this email and a temporary password. If left
            unchecked, their account is created but they cannot log in until access is granted later.
          </CheckboxHelperText>
        </FormField>

        <FooterNote>{t('addUser.footerNote')}</FooterNote>

        <ModalFooter>
          <Button variant="secondary" onClick={handleClose}>
            {t('addUser.cancelButton')}
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            isLoading={createMutation.isPending}
            disabled={createMutation.isPending}
          >
            {t('addUser.submitButton')}
          </Button>
        </ModalFooter>
      </Modal>

      <TempPasswordModal
        isOpen={tempPassword !== null}
        onClose={handleTempPasswordClose}
        temporaryPassword={tempPassword}
        userLabel={tempPasswordLabel}
      />
    </>
  );
};

export default AddUserModal;
